import { useState } from 'react';
import { breakEvenScenarios } from '../data';

export default function BreakEvenSection() {
  const [selected, setSelected] = useState(0);

  const scenario = breakEvenScenarios[selected];

  const spreadOnlyTotal = scenario.tradesPerMonth * scenario.lotSize * scenario.spreadOnlySpread * 10;
  const ecnSpreadCost = scenario.tradesPerMonth * scenario.lotSize * scenario.ecnSpread * 10;
  const ecnCommissionCost = scenario.tradesPerMonth * scenario.lotSize * scenario.ecnCommissionPerSide * 2;
  const ecnTotal = ecnSpreadCost + ecnCommissionCost;
  const savings = spreadOnlyTotal - ecnTotal;
  const savingsPct = spreadOnlyTotal > 0 ? (savings / spreadOnlyTotal) * 100 : 0;

  const maxCost = Math.max(spreadOnlyTotal, ecnTotal);
  const spreadOnlyPct = Math.max(8, (spreadOnlyTotal / maxCost) * 100);
  const ecnTotalPct = Math.max(8, (ecnTotal / maxCost) * 100);
  const ecnSpreadPct = (ecnSpreadCost / ecnTotal) * 100;
  const ecnCommissionPct = (ecnCommissionCost / ecnTotal) * 100;

  const labels = ['Scalper', 'Day Trader', 'Swing Trader', 'Position Trader'];
  const subtitles = [
    '15 round-turns / day · 0.1 lots',
    '5 round-turns / day · 0.5 lots',
    '1 round-turn / day · 2.0 lots',
    '~1.5 round-turns / week · 5.0 lots',
  ];

  const colorRoles = ['primary', 'accent', 'secondary', 'foreground'] as const;
  const role = colorRoles[selected];

  return (
    <section className="relative w-full bg-background-100 overflow-hidden">
      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-12 md:py-18">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-10 mb-10 md:mb-14">
          <div className="flex-1">
            <h2 className="text-[28px] md:text-[38px] font-[750] leading-[36px] md:leading-[46px] text-foreground-950 tracking-[-0.015em] max-w-[700px]">
              At what volume does ECN become cheaper?
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 lg:gap-12">
          {/* Left: Style selector */}
          <div className="space-y-1">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-foreground-400 mb-4">
              Select your style
            </div>
            {breakEvenScenarios.map((s, i) => (
              <button
                key={i}
                onClick={() => setSelected(i)}
                className={`w-full text-left px-4 py-3.5 rounded-xl transition-all duration-200 cursor-pointer group ${
                  i === selected
                    ? `bg-${role}-50/60`
                    : 'hover:bg-background-50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-[3px] h-8 rounded-full transition-all duration-200 ${
                      i === selected ? `bg-${role}-500` : 'bg-background-200'
                    }`}
                  />
                  <div>
                    <div
                      className={`text-[14px] font-[700] transition-colors ${
                        i === selected ? 'text-foreground-950' : 'text-foreground-600 group-hover:text-foreground-800'
                      }`}
                    >
                      {labels[i]}
                    </div>
                    <div className="text-[12px] text-foreground-400 mt-0.5">
                      {subtitles[i]}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Right: Comparison panel */}
          <div className="relative">
            {/* Panel header */}
            <div className="mb-8 md:mb-10">
              <h3 className="text-[18px] md:text-[20px] font-[750] text-foreground-950 tracking-[-0.01em]">
                {labels[selected]} — Monthly Cost
              </h3>
              <p className="text-[13px] text-foreground-500 mt-1">
                {scenario.tradesPerMonth} trades × {scenario.lotSize} lots × {scenario.spreadOnlySpread} pips spread
              </p>
            </div>

            {/* Cost comparison rows */}
            <div className="space-y-8 md:space-y-10">
              {/* Spread-Only */}
              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <div>
                    <div className="text-[13px] font-[700] text-foreground-700">Spread-Only Account</div>
                    <div className="text-[12px] text-foreground-400 mt-0.5">All cost baked into spread</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[28px] md:text-[32px] font-[750] text-foreground-950 leading-none tracking-[-0.02em]">
                      ${spreadOnlyTotal.toFixed(0)}
                      <span className="text-[14px] font-[600] text-foreground-400 ml-1">/mo</span>
                    </div>
                  </div>
                </div>
                <div className="h-2 bg-background-200/60 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-400 rounded-full transition-all duration-700"
                    style={{ width: `${spreadOnlyPct}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2">
                  <span className="text-[11px] text-foreground-400">${spreadOnlyTotal.toFixed(0)} spread</span>
                  <span className="text-[11px] text-foreground-400">100%</span>
                </div>
              </div>

              {/* ECN */}
              <div>
                <div className="flex items-baseline justify-between mb-3">
                  <div>
                    <div className="text-[13px] font-[700] text-foreground-700">ECN Commission Account</div>
                    <div className="text-[12px] text-foreground-400 mt-0.5">Raw spread + separate commission</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[28px] md:text-[32px] font-[750] text-accent-600 leading-none tracking-[-0.02em]">
                      ${ecnTotal.toFixed(0)}
                      <span className="text-[14px] font-[600] text-accent-400 ml-1">/mo</span>
                    </div>
                  </div>
                </div>
                <div className="h-2 bg-background-200/60 rounded-full overflow-hidden flex">
                  <div
                    className="h-full bg-accent-300 rounded-full transition-all duration-700"
                    style={{ width: `${(ecnTotalPct * ecnSpreadPct) / 100}%` }}
                  />
                  <div
                    className="h-full bg-accent-500 rounded-full transition-all duration-700"
                    style={{ width: `${(ecnTotalPct * ecnCommissionPct) / 100}%` }}
                  />
                </div>
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-accent-300" />
                    <span className="text-[11px] text-foreground-400">${ecnSpreadCost.toFixed(0)} spread</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-accent-500" />
                    <span className="text-[11px] text-foreground-400">${ecnCommissionCost.toFixed(0)} commission</span>
                  </div>
                  <div className="ml-auto text-[11px] text-foreground-400">
                    {((ecnSpreadCost / ecnTotal) * 100).toFixed(0)}% / {((ecnCommissionCost / ecnTotal) * 100).toFixed(0)}%
                  </div>
                </div>
              </div>
            </div>

            {/* Verdict strip */}
            <div
              className={`mt-10 md:mt-12 pt-6 border-t ${
                savings > 0 ? 'border-accent-200/60' : 'border-background-200/70'
              }`}
            >
              <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4">
                {savings > 0 ? (
                  <>
                    <span className="text-[20px] md:text-[24px] font-[750] text-accent-600 tracking-[-0.01em]">
                      ECN saves ${savings.toFixed(0)}/month
                    </span>
                    <span className="text-[14px] font-[600] text-accent-400">
                      ({savingsPct.toFixed(0)}% cheaper)
                    </span>
                  </>
                ) : (
                  <span className="text-[20px] md:text-[24px] font-[750] text-foreground-600 tracking-[-0.01em]">
                    Costs are roughly equal
                  </span>
                )}
              </div>
              <p className="text-[13px] text-foreground-500 mt-2 max-w-[520px]">
                {savings > 50
                  ? `At this volume, the ECN account is clearly the better choice. The savings add up to $${(savings * 12).toFixed(0)}/year.`
                  : savings > 10
                    ? 'The ECN account has a small but consistent edge. Worth switching if you trade this volume regularly.'
                    : 'At this volume, the difference is negligible — under $10/month. Choose based on platform preference, not cost.'}
              </p>
            </div>

            {/* Formula breakdown */}
            <div className="mt-8 md:mt-10 pt-6 border-t border-background-200/70">
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-foreground-400 mb-4">
                Cost formula
              </div>
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
                  <span className="text-[12px] font-[700] text-foreground-600 w-28 shrink-0">Spread-Only</span>
                  <span className="text-[13px] text-foreground-500 font-mono">
                    {scenario.tradesPerMonth} × {scenario.lotSize} × {scenario.spreadOnlySpread} pips × $10
                  </span>
                  <span className="text-[13px] font-[700] text-foreground-800 sm:ml-auto">
                    = ${spreadOnlyTotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
                  <span className="text-[12px] font-[700] text-accent-600 w-28 shrink-0">ECN Spread</span>
                  <span className="text-[13px] text-foreground-500 font-mono">
                    {scenario.tradesPerMonth} × {scenario.lotSize} × {scenario.ecnSpread} pips × $10
                  </span>
                  <span className="text-[13px] font-[700] text-foreground-800 sm:ml-auto">
                    = ${ecnSpreadCost.toFixed(2)}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-3">
                  <span className="text-[12px] font-[700] text-accent-600 w-28 shrink-0">ECN Commission</span>
                  <span className="text-[13px] text-foreground-500 font-mono">
                    {scenario.tradesPerMonth} × {scenario.lotSize} × ${(scenario.ecnCommissionPerSide * 2).toFixed(2)} RT
                  </span>
                  <span className="text-[13px] font-[700] text-foreground-800 sm:ml-auto">
                    = ${ecnCommissionCost.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}